/* eslint-disable max-lines */
import { NetworkStatuses } from '@strata/constants';
import { DateFormats, formatDate, sortAsc } from '@strata/helper-functions';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';

import { handleError } from 'services/ErrorManagement';
import { get, useGet } from 'services/Http';
import { ListSchema, ListType, useListTypeOptions } from 'services/ListManagementHelpers';
import { mapSuppliersToOptions } from 'services/NPISupport';

export const ConditionType = {
    ACCOUNT_LIST: 'account_list_id',
    DIAGNOSIS: 'dx_oid',
    GROUP_LIST: 'group_list_id',
    NETWORK_LIST: 'network_list_id_condition',
    SUPPLIER: 'supplier_npi_condition',
    PLACE_OF_SERVICE: 'place_of_service',
    PRESCRIBER: 'prescriber_npi',
    PRESCRIBER_LIST: 'prescriber_list_id',
    PRODUCT: 'product_service_oid',
    PRODUCT_LIST: 'product_list_ids',
    PRODUCT_TYPE: 'product_type_condition_is_brand',
    SINGLE_SERVICING_SUPPLIER: 'single_service_supplier_condition',
    THERAPEUTIC_CATEGORY_LIST: 'therapeutic_category_list_id',
    ZIP_CODE_LIST: 'zip_code_list_id',
};

export const ConditionTypeNameMap = {
    [ConditionType.ACCOUNT_LIST]: 'Account List',
    [ConditionType.DIAGNOSIS]: 'Diagnosis',
    [ConditionType.GROUP_LIST]: 'Group List',
    [ConditionType.NETWORK_LIST]: 'Network List',
    [ConditionType.SUPPLIER]: 'Supplier',
    [ConditionType.PLACE_OF_SERVICE]: 'Place Of Service',
    [ConditionType.PRESCRIBER]: 'Prescriber',
    [ConditionType.PRESCRIBER_LIST]: 'Prescriber List',
    [ConditionType.PRODUCT]: 'Product',
    [ConditionType.PRODUCT_LIST]: 'Product List(s)',
    [ConditionType.PRODUCT_TYPE]: 'Product Type',
    [ConditionType.SINGLE_SERVICING_SUPPLIER]: 'Sole Servicing Supplier',
    [ConditionType.THERAPEUTIC_CATEGORY_LIST]: 'Therapeutic Category List',
    [ConditionType.ZIP_CODE_LIST]: 'Zip Code List',
};

/** Product type enum for Product type and Product conditions */
export const ProductType = {
    BRAND: 'Brand',
    GENERIC: 'Generic',
};

/** Convert boolean product type value to its display value */
export const getSelectedProductType = (isBrand) => {
    if (isBrand == null) {
        return undefined;
    }
    return isBrand ? ProductType.BRAND : ProductType.GENERIC;
};

export const SendToOptions = {
    NETWORK_LIST: 'NETWORK_LIST',
    PHARMACY: 'PHARMACY',
    INFERRED_SUPPLIER: 'INFERRED_SUPPLIER',
};
/** Gets the display name for the inferred supplier send to option */
export const getInferredSupplierDisplayName = (values) => {
    const hasSoleServicingSupplierCondition = values?.conditions?.find(
        (condition) =>
            condition.condition_type === ConditionType.SINGLE_SERVICING_SUPPLIER &&
            condition.value === true,
    );
    return hasSoleServicingSupplierCondition
        ? 'Sole Servicing Supplier'
        : 'Servicing Supplier On Auth/Claim';
};

export const RouteGroup = {
    INITIAL_ROUTES: 0,
    BEFORE_CONTINUITY: 1,
    CONTINUITY: 2,
    AFTER_CONTINUITY: 3,
    FINAL_ROUTES: 4,
    POST_LIVE_REFERRAL_ASSIGNMENT: 5,
};

export const ContinuityOfCareRoute = {
    FIRST_REFUSAL: 'first_refusal',
    CLAIMS_CHECK: 'claims_check',
    FOLLOW_ON: 'follow_on',
    MEMBER_PREFERENCE: 'patient_relationship',
    THERAPY_CONTINUITY: 'therapy_continuity',
    FIRST_REFUSAL_CLAIM: 'first_refusal_claim',
};

const PatientRelationshipRoutes = [
    ContinuityOfCareRoute.MEMBER_PREFERENCE,
    ContinuityOfCareRoute.THERAPY_CONTINUITY,
];

export const PatientRelationshipType = {
    MEM_PREF: 'MEM_PREF',
    THRPY_CONT: 'THRPY_CONT',
};

/** Base API endpoint for patient relationship routes */
export const patientRelationshipRoutesUrl = (payerId, segmentId, patientRelationshipType) =>
    `/clients/admin/${payerId}/${segmentId}/patient_relationship_routes/${patientRelationshipType}`;

export const PatientRelationshipTypeDisplayNameMap = {
    [PatientRelationshipType.MEM_PREF]: 'Patient Preference',
    [PatientRelationshipType.THRPY_CONT]: 'Therapy Continuity',
};
export const PatientRelationshipTypeDisplayNameMapPluralized = {
    [PatientRelationshipType.MEM_PREF]: 'Patient Preferences',
    [PatientRelationshipType.THRPY_CONT]: 'Therapy Continuities',
};

/** Display text when no therapeutic category is selected for a patient preference route */
export const NO_THERAPEUTIC_CATEGORY_DISPLAY_TEXT = 'Any/all categories';

/** Get the display text for a patient relationship entry */
export const getPatientRelationshipDisplayText = (preference) =>
    preference
        ? `${preference.member_id} - ${preference.therapeutic_category ?? NO_THERAPEUTIC_CATEGORY_DISPLAY_TEXT} - ${preference.organization_name}`
        : undefined;

/** Check if patient preference routes are duplicates */
export const isSamePatientPreference = (a, b) =>
    a.member_id === b.member_id &&
    (a.therapeutic_category ?? undefined) === (b.therapeutic_category ?? undefined);

/** Continuity of Care Claims Check Route Modes */
export const ClaimsCheckMode = {
    ALL: 'ALL',
    DRUG: 'DRUG',
    THERAPY: 'THERAPY',
};

export const routeRulesUrl = (segmentId, benefit) =>
    `/route-management/route-rules/${segmentId ?? ''}/${benefit ?? ''}`;

/** API endpoint for every global route */
export const globalRoutesUrl = '/assignment/global_routes';

/** API endpoint for the shared rule of one global route */
export const globalRouteUrl = (globalRouteId) => `${globalRoutesUrl}/${globalRouteId}`;

/**
 * API endpoint for the payer segments a global route is configured in. Segment membership lives
 * in each payer's own database, not with the shared rule, so it is read from the client API.
 */
export const globalRouteSegmentsUrl = (benefit, globalRouteId) =>
    `/clients/admin/route_rules/${benefit}/${globalRouteId}/payer_segments`;

/** API endpoint pairing every global route with each payer segment using it */
export const globalRouteSegmentUsageUrl = '/clients/admin/route_rules/payer_segments';

/** App route listing every global route, of every benefit */
export const globalRoutesAppRoute = '/route-management/global-routes';

export const globalRouteAppRoute = (globalRouteId) => `${globalRoutesAppRoute}/${globalRouteId}`;

export const reorderRoutes = (dataGroups) => {
    let currDisplaySequence = 0;
    return dataGroups?.map((group, i) => ({
        ...group,
        data: group.data?.map((route, j) => {
            currDisplaySequence += 1;
            return {
                ...route,
                route_sequence: j,
                before_continuity:
                    i === RouteGroup.INITIAL_ROUTES || i === RouteGroup.BEFORE_CONTINUITY,
                route_group: i,
                display_sequence: currDisplaySequence,
            };
        }),
    }));
};

export const getRoutes = async (payerId, segmentId, benefit) => {
    const [configuredRoutes, standardRoutes] = await Promise.all([
        get(`/clients/admin/${payerId}/${segmentId}/route_rules/${benefit}`),
        get(`/clients/admin/${payerId}/${segmentId}/route_rules/${benefit}/standard`),
    ]);
    return reorderRoutes([
        {
            data: standardRoutes?.initial.map((route) => ({
                ...route,
                can_edit: PatientRelationshipRoutes.includes(route.route_rule_id),
                can_delete: false,
            })),
            droppable: false,
        },
        {
            data: configuredRoutes
                ?.filter((route) => route.before_continuity)
                .sort((a, b) =>
                    sortAsc(a.route_sequence?.toString(), b.route_sequence?.toString()),
                ),
        },
        {
            header: 'Continuity Of Care Start',
            droppable: false,
            data: standardRoutes?.continuity_of_care.map((route) => ({
                ...route,
                can_edit: ![
                    ContinuityOfCareRoute.FIRST_REFUSAL,
                    ContinuityOfCareRoute.FOLLOW_ON,
                ].includes(route.route_rule_id),
                can_delete: false,
            })),
        },
        {
            header: 'Continuity Of Care End',
            data: configuredRoutes
                ?.filter((route) => !route.before_continuity)
                .sort((a, b) =>
                    sortAsc(a.route_sequence?.toString(), b.route_sequence?.toString()),
                ),
        },
        {
            data: standardRoutes?.final.map((route) => ({
                ...route,
                can_edit: false,
                can_delete: false,
            })),
            droppable: false,
        },
        {
            header: 'Post Live Referral Assignment',
            droppable: false,
            data: [
                {
                    isPostLiveReferralAssignment: true,
                    is_active: true,
                    can_edit: false,
                    can_delete: false,
                },
            ],
        },
    ]);
};

/** Determine if route option should be disabled when 'After' is selected */
export const getDisabledAfter = (groupIndex, groupRoutesLength, routeIndex) => {
    // Can't be placed after last group of routes
    if (groupIndex === RouteGroup.FINAL_ROUTES) {
        return true;
    }
    // Can only be placed after last route in first group of routes & Continuity
    return (
        [RouteGroup.INITIAL_ROUTES, RouteGroup.CONTINUITY].includes(groupIndex) &&
        routeIndex !== groupRoutesLength - 1
    );
};

/** Determine if route option should be disabled when 'Before' is selected */
const getDisabledBefore = (groupIndex, routeIndex) => {
    // Can't be placed before first group of routes
    if (groupIndex === RouteGroup.INITIAL_ROUTES) {
        return true;
    }
    // Can only be placed before first route in Continuity & last group of routes
    return (
        [RouteGroup.CONTINUITY, RouteGroup.FINAL_ROUTES].includes(groupIndex) && routeIndex !== 0
    );
};

/** Determine if route option should be disabled when 'Replace' is selected. Only configured rules can be replaced. */
const getDisabledReplace = (groupIndex) =>
    ![RouteGroup.BEFORE_CONTINUITY, RouteGroup.AFTER_CONTINUITY].includes(groupIndex);

export const SequenceOptions = {
    BEFORE: 'Before',
    AFTER: 'After',
    REPLACE: 'Replace',
};

/** An end date is inclusive: a route runs through the end of that day, and has expired after it */
export const isRouteExpired = (endDate) => !!endDate && dayjs().isAfter(endDate, 'day');

/** Get a segment's routes as Rule Placement options. An expired route cannot be placed against */
export const getRulePlacementOptions = async (payerId, segmentId, benefit) =>
    (await getRoutes(payerId, segmentId, benefit)).flatMap((group, i) => {
        const placeableRoutes = group.data.filter(
            (route) => !route.isPostLiveReferralAssignment && !isRouteExpired(route.end_date),
        );
        return placeableRoutes.map((route, j) => ({
            value: route.route_rule_id,
            name: `${route.display_sequence}. ${route.route_name}`,
            route_sequence: route.route_sequence,
            before_continuity: route.before_continuity,
            groupIndex: i,
            disabledBefore: getDisabledBefore(i, j),
            disabledAfter: getDisabledAfter(i, placeableRoutes.length, j),
            disabledReplace: getDisabledReplace(i),
        }));
    });

/**
 * Determine where a rule lands in a segment's route order for the selected sequence and rule
 * placement. `existing` is the rule's current placement in that segment, when it already has one.
 */
export const getRouteSequencing = ({ routes, sequence, rulePlacement, existing = undefined }) => {
    const placementIndex = routes?.findIndex(({ value }) => value === rulePlacement);
    const placement = placementIndex >= 0 ? routes[placementIndex] : undefined;

    if (!placement) {
        return { before_continuity: undefined, route_sequence: undefined };
    }

    const beforeContinuity = (() => {
        if (sequence === SequenceOptions.REPLACE) {
            return placement.before_continuity;
        }
        if (sequence === SequenceOptions.BEFORE) {
            // Route is before first CoC route
            if (placement.groupIndex === RouteGroup.CONTINUITY) {
                return true;
            }
            // Route is before first final route
            if (placement.groupIndex === RouteGroup.FINAL_ROUTES) {
                return false;
            }
        }
        // Route is after last initial route
        if (placement.groupIndex === RouteGroup.INITIAL_ROUTES) {
            return true;
        }
        // Route is after last CoC route
        if (placement.groupIndex === RouteGroup.CONTINUITY) {
            return false;
        }
        return placement.before_continuity;
    })();

    const routeSequence = (() => {
        if (sequence === SequenceOptions.REPLACE) {
            return placement.route_sequence;
        }
        if (sequence === SequenceOptions.BEFORE) {
            if ([RouteGroup.CONTINUITY, RouteGroup.FINAL_ROUTES].includes(placement.groupIndex)) {
                const previousPlacement = routes[placementIndex - 1];
                // Route is before first CoC route or first final route and there are other custom routes
                if (
                    [RouteGroup.BEFORE_CONTINUITY, RouteGroup.AFTER_CONTINUITY].includes(
                        previousPlacement.groupIndex,
                    )
                ) {
                    return previousPlacement.route_sequence + 1;
                }
                // Route is before first CoC route or first final route and there are no custom routes
                return 0;
            }

            // Existing rule and same before continuity section
            if (existing?.before_continuity === beforeContinuity) {
                return Math.max(0, placement.route_sequence - 1);
            }

            return placement.route_sequence;
        }

        // Route is after last initial route or CoC route
        if ([RouteGroup.INITIAL_ROUTES, RouteGroup.CONTINUITY].includes(placement.groupIndex)) {
            return 0;
        }

        // Existing rule, same before continuity section, and greater than existing sequence
        if (
            existing?.before_continuity === beforeContinuity &&
            placement.route_sequence > existing.route_sequence
        ) {
            return placement.route_sequence;
        }

        return placement.route_sequence + 1;
    })();

    return { before_continuity: beforeContinuity, route_sequence: routeSequence };
};

/** Rule placement options of every segment a rule is configured in, keyed by segment id */
export const useConfiguredSegmentRoutes = (configuredSegments, benefit) => {
    // The segment ids, not the fetched data they come from, determine which routes to load
    const segmentKeys = configuredSegments
        ?.filter(({ payer_id: payerId, segment_id: segmentId }) => payerId && segmentId)
        .map(({ payer_id: payerId, segment_id: segmentId }) => `${payerId}/${segmentId}`)
        .join();

    const [routesBySegment, setRoutesBySegment] = useState();
    useEffect(() => {
        if (!segmentKeys || !benefit) {
            return;
        }
        void (async () => {
            try {
                const segmentRoutes = await Promise.all(
                    segmentKeys.split(',').map(async (key) => {
                        const [payerId, segmentId] = key.split('/');
                        return [
                            segmentId,
                            await getRulePlacementOptions(payerId, segmentId, benefit),
                        ];
                    }),
                );
                setRoutesBySegment(Object.fromEntries(segmentRoutes));
            } catch (err) {
                setRoutesBySegment({});
                handleError('An error occurred loading route rules.', err);
            }
        })();
    }, [segmentKeys, benefit]);

    return routesBySegment;
};

/** Where a rule sits in one payer segment, and for how long, as the API expects it */
export const getSegmentPlacement = (segment) => ({
    before_continuity: segment.before_continuity,
    route_sequence: segment.route_sequence,
    start_date: formatDate(segment.start_date, DateFormats.HYPHEN_DATE),
    end_date: segment.end_date ? formatDate(segment.end_date, DateFormats.HYPHEN_DATE) : undefined,
    // A replacing rule takes the sequence of the rule it is placed on, which gives way to it
    ...(segment.sequence === SequenceOptions.REPLACE && { replace: true }),
});

/**
 * Options for every route rule condition. Products are configured per payer segment, so they come
 * from the segment the rule is being edited in. A global route applies to segments across payers,
 * so it offers every product instead.
 */
export const useConditionOptions = (segmentId, benefit, isGlobalRoute = false) => {
    const accountLists = useListTypeOptions(ListSchema.assignment, ListType.account);
    const groupLists = useListTypeOptions(ListSchema.assignment, ListType.group);
    const networkLists = useListTypeOptions(ListSchema.assignment, ListType.network);
    const prescriberLists = useListTypeOptions(ListSchema.assignment, ListType.prescriber);
    const productLists = useListTypeOptions(ListSchema.assignment, ListType.product);
    const therapeuticCategoryLists = useListTypeOptions(
        ListSchema.assignment,
        ListType.therapeutic_category,
    );
    const zipCodeLists = useListTypeOptions(ListSchema.assignment, ListType.zip_code);

    const segmentProductsUrl = segmentId
        ? `/reference/product_benefit_configs/segments/${segmentId}`
        : null;
    const products = useGet(
        isGlobalRoute ? '/reference/product_benefit_configs/' : segmentProductsUrl,
    );
    const productOptions = useMemo(
        () =>
            products
                // Every product is offered for a global route, with no one segment's benefit to filter on
                ?.filter((product) => isGlobalRoute || product.benefit === benefit)
                .map((product) => {
                    const oid = isGlobalRoute ? product.oid : product.product_service_oid;
                    return {
                        name: product.description,
                        value: oid,
                        secondaryData: oid,
                        brandProductDescription: product.brand_product_description,
                        genericProductDescription: product.generic_product_description,
                    };
                })
                .sort((a, b) => sortAsc(a.name, b.name)),
        [products, benefit, isGlobalRoute],
    );

    const diagnoses = useGet('/reference/diagnoses/');
    const diagnosisOptions = useMemo(
        () =>
            diagnoses
                ?.map((diagnosis) => ({
                    name: diagnosis.description,
                    value: diagnosis.oid,
                    secondaryData: diagnosis.oid,
                    codes: diagnosis.codes?.map((code) => code.code).join(', '),
                }))
                .sort((a, b) => sortAsc(a.name, b.name)),
        [diagnoses],
    );

    const placesOfService = useGet('/reference/product_benefit_configs/places');
    const placeOfServiceOptions = placesOfService?.map((place) => ({
        name: place.description,
        value: place.code,
    }));

    const suppliers = useGet(`/reference/suppliers/parents`);
    const supplierOptions = useMemo(
        () =>
            mapSuppliersToOptions(suppliers)?.filter(
                (supplier) => supplier.network_status?.trim() !== NetworkStatuses.INACTIVE,
            ),
        [suppliers],
    );

    return {
        networkLists,
        supplierOptions,
        conditionOptionsMap: {
            [ConditionType.ACCOUNT_LIST]: accountLists,
            [ConditionType.DIAGNOSIS]: diagnosisOptions,
            [ConditionType.GROUP_LIST]: groupLists,
            [ConditionType.NETWORK_LIST]: networkLists,
            [ConditionType.SUPPLIER]: supplierOptions,
            [ConditionType.PLACE_OF_SERVICE]: placeOfServiceOptions,
            [ConditionType.PRESCRIBER_LIST]: prescriberLists,
            [ConditionType.PRODUCT]: productOptions,
            [ConditionType.PRODUCT_LIST]: productLists,
            [ConditionType.THERAPEUTIC_CATEGORY_LIST]: therapeuticCategoryLists,
            [ConditionType.ZIP_CODE_LIST]: zipCodeLists,
        },
    };
};

/**
 * Placement a segment's existing rule pre-populates with: before the route that currently follows
 * it in that segment's route list.
 */
export const getInitialPlacement = (routes, existing) => {
    const ruleIndex = routes?.findIndex(({ value }) => value === existing?.route_rule_id);
    const rulePlacement = routes?.[ruleIndex + 1]?.value;

    return {
        sequence: SequenceOptions.BEFORE,
        rule_placement: rulePlacement,
        ...getRouteSequencing({
            routes,
            sequence: SequenceOptions.BEFORE,
            rulePlacement,
            existing,
        }),
    };
};
